import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, Plus, Clock, Trash2, Edit2, Save, X, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';

const Timesheets = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek());
  const [editingId, setEditingId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [timesheetForm, setTimesheetForm] = useState({
    task_id: '',
    date: new Date().toISOString().split('T')[0],
    hours: '',
    work_type: 'regular',
    description: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchProjectTasks();
      fetchTimesheets();
      fetchSummary();
    }
  }, [selectedProject, selectedWeek]);

  function getCurrentWeek() {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + 1); // Monday
    return startOfWeek.toISOString().split('T')[0];
  }

  const fetchProjects = async () => {
    try {
      const response = await axiosInstance.get('/projects');
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
      }
    } catch (error) {
      toast.error(t('dataLoadFailed'));
    }
  };

  const fetchProjectTasks = async () => {
    if (!selectedProject) return;
    
    try {
      const response = await axiosInstance.get(`/projects/${selectedProject}/tasks`);
      setTasks(response.data);
    } catch (error) {
      console.error('Tasks fetch failed:', error);
    }
  };

  const fetchTimesheets = async () => {
    setLoading(true);
    try {
      const weekStart = new Date(selectedWeek);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const params = new URLSearchParams({
        date_from: weekStart.toISOString().split('T')[0],
        date_to: weekEnd.toISOString().split('T')[0]
      });

      if (selectedProject) {
        params.append('project_id', selectedProject);
      }

      const response = await axiosInstance.get(`/timesheets?${params.toString()}`);
      setTimesheets(response.data);
    } catch (error) {
      console.error('Timesheets fetch failed:', error);
      toast.error(t('dataLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    if (!selectedProject) return;
    
    try {
      const weekStart = new Date(selectedWeek);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const params = new URLSearchParams({
        project_id: selectedProject,
        date_from: weekStart.toISOString().split('T')[0],
        date_to: weekEnd.toISOString().split('T')[0]
      });

      const response = await axiosInstance.get(`/timesheets/summary?${params.toString()}`);
      setSummary(response.data);
    } catch (error) {
      console.error('Summary fetch failed:', error);
    }
  };

  const handleCreateTimesheet = async (e) => {
    e.preventDefault();
    
    if (!selectedProject || !timesheetForm.hours) {
      toast.error(t('pleaseEnterProjectAndHours'));
      return;
    }

    try {
      if (editingId) {
        await axiosInstance.put(`/timesheets/${editingId}`, {
          task_id: timesheetForm.task_id || null,
          hours: parseFloat(timesheetForm.hours),
          work_type: timesheetForm.work_type,
          description: timesheetForm.description
        });
        toast.success(t('timesheetUpdated'));
      } else {
        await axiosInstance.post('/timesheets', {
          project_id: selectedProject,
          task_id: timesheetForm.task_id || null,
          date: timesheetForm.date,
          hours: parseFloat(timesheetForm.hours),
          work_type: timesheetForm.work_type,
          description: timesheetForm.description
        });
        toast.success(t('timesheetSaved'));
      }
      
      resetForm();
      fetchTimesheets();
      fetchSummary();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'İşlem başarısız';
      toast.error(errorMsg);
    }
  };

  const handleEdit = (timesheet) => {
    setEditingId(timesheet.id);
    setTimesheetForm({
      task_id: timesheet.task_id || '',
      date: timesheet.date,
      hours: timesheet.hours.toString(),
      work_type: timesheet.work_type || 'regular',
      description: timesheet.description || ''
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('deleteConfirm'))) return;
    
    try {
      await axiosInstance.delete(`/timesheets/${id}`);
      toast.success(t('timesheetDeleted'));
      fetchTimesheets();
      fetchSummary();
    } catch (error) {
      toast.error(t('deleteFailed'));
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setTimesheetForm({
      task_id: '',
      date: new Date().toISOString().split('T')[0],
      hours: '',
      work_type: 'regular',
      description: ''
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { icon: AlertCircle, color: 'bg-yellow-100 text-yellow-800', text: t('pending') },
      approved: { icon: CheckCircle, color: 'bg-green-100 text-green-800', text: t('approved') },
      rejected: { icon: XCircle, color: 'bg-red-100 text-red-800', text: t('rejected') }
    };
    const variant = variants[status] || variants.pending;
    const Icon = variant.icon;
    return (
      <Badge className={`${variant.color} flex items-center gap-1`}>
        <Icon size={12} />
        {variant.text}
      </Badge>
    );
  };

  const getWorkTypeLabel = (workType) => {
    const labels = {
      regular: t('normal'),
      overtime: t('overtime'),
      weekend: t('weekend'),
      holiday: t('holiday')
    };
    return labels[workType] || t('normal');
  };

  const getTaskById = (taskId) => {
    return tasks.find(t => t.id === taskId);
  };

  const getTotalHours = () => {
    return timesheets.reduce((sum, ts) => sum + ts.hours, 0);
  };

  const getWeekDays = () => {
    const days = [];
    const start = new Date(selectedWeek);
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getTimesheetsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return timesheets.filter(ts => ts.date === dateStr);
  };

  const changeWeek = (direction) => {
    const current = new Date(selectedWeek);
    current.setDate(current.getDate() + (direction * 7));
    setSelectedWeek(current.toISOString().split('T')[0]);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Clock className="h-8 w-8" />
                {t('workingHours')}
              </h1>
              <p className="text-gray-600 mt-1">{t('logDailyHours')}</p>
            </div>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              {t('backToDashboard')}
            </Button>
          </div>

          {/* Filters */}
          <div className="flex gap-4 flex-wrap">
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder={t('selectProject')} />
              </SelectTrigger>
              <SelectContent>
                {projects.map(project => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => changeWeek(-1)}>
                ← {t('previousWeek')}
              </Button>
              <span className="text-sm font-medium px-4">
                {new Date(selectedWeek).toLocaleDateString('tr-TR')} - {new Date(new Date(selectedWeek).setDate(new Date(selectedWeek).getDate() + 6)).toLocaleDateString('tr-TR')}
              </span>
              <Button variant="outline" size="sm" onClick={() => changeWeek(1)}>
                {t('nextWeek')} →
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('totalHours')} ({t('week')})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{summary?.total_hours?.toFixed(1) || '0.0'}h</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('regularHours')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{summary?.regular_hours?.toFixed(1) || '0.0'}h</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('overtimeHours')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{summary?.overtime_hours?.toFixed(1) || '0.0'}h</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('recordCount')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{timesheets.length}</div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <Card>
            <CardHeader>
              <CardTitle>{editingId ? t('edit') : t('newRecord')}</CardTitle>
              <CardDescription>
                {t('logDailyHours')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateTimesheet} className="space-y-4">
                <div>
                  <Label>{t('taskOptional')}</Label>
                  <Select
                    value={timesheetForm.task_id || undefined}
                    onValueChange={(value) => setTimesheetForm({...timesheetForm, task_id: value || ''})}
                    disabled={editingId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('selectTaskOrLeaveEmpty')} />
                    </SelectTrigger>
                    <SelectContent>
                      {tasks.map(task => (
                        <SelectItem key={task.id} value={task.id}>
                          {task.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">{t('noTaskSelected')}</p>
                </div>

                <div>
                  <Label>{t('date')} <span className="text-red-500">*</span></Label>
                  <Input
                    type="date"
                    value={timesheetForm.date}
                    onChange={(e) => setTimesheetForm({...timesheetForm, date: e.target.value})}
                    disabled={editingId}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div>
                  <Label>{t('hours')} <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0.5"
                    max="24"
                    value={timesheetForm.hours}
                    onChange={(e) => setTimesheetForm({...timesheetForm, hours: e.target.value})}
                    placeholder="8"
                    required
                  />
                </div>

                <div>
                  <Label>{t('workType')}</Label>
                  <Select
                    value={timesheetForm.work_type}
                    onValueChange={(value) => setTimesheetForm({...timesheetForm, work_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="regular">{t('normalWork')}</SelectItem>
                      <SelectItem value="overtime">{t('overtime')}</SelectItem>
                      <SelectItem value="weekend">{t('weekendWork')}</SelectItem>
                      <SelectItem value="holiday">{t('holidayWork')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>{t('description')}</Label>
                  <Textarea
                    value={timesheetForm.description}
                    onChange={(e) => setTimesheetForm({...timesheetForm, description: e.target.value})}
                    placeholder={t('description')}
                    rows={3}
                  />
                </div>

                <div className="flex gap-2">
                  {editingId && (
                    <Button type="button" variant="outline" onClick={resetForm} className="flex-1">
                      <X size={16} className="mr-2" />
                      {t('cancel')}
                    </Button>
                  )}
                  <Button type="submit" className="flex-1">
                    <Save size={16} className="mr-2" />
                    {editingId ? t('update') : t('save')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Weekly View */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>{t('weeklyView')}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">Yükleniyor...</div>
              ) : (
                <div className="space-y-4">
                  {getWeekDays().map(day => {
                    const dayTimesheets = getTimesheetsForDate(day);
                    const dayTotal = dayTimesheets.reduce((sum, ts) => sum + ts.hours, 0);
                    const isToday = day.toDateString() === new Date().toDateString();
                    
                    return (
                      <div key={day.toISOString()} className={`border rounded-lg p-4 ${isToday ? 'border-blue-500 bg-blue-50' : ''}`}>
                        <div className="flex justify-between items-center mb-2">
                          <div>
                            <div className="font-semibold">
                              {day.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })}
                            </div>
                            {isToday && <span className="text-xs text-blue-600">{t('today')}</span>}
                          </div>
                          <div className="text-lg font-bold">{dayTotal.toFixed(1)}h</div>
                        </div>
                        
                        {dayTimesheets.length === 0 ? (
                          <div className="text-sm text-gray-500">{t('noRecords')}</div>
                        ) : (
                          <div className="space-y-2">
                            {dayTimesheets.map(ts => {
                              const task = getTaskById(ts.task_id);
                              return (
                                <div key={ts.id} className="flex items-start justify-between bg-white p-3 rounded border">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <div className="text-sm font-medium">{task?.title || t('projectLevel')}</div>
                                      <Badge variant="outline" className="text-xs">
                                        {getWorkTypeLabel(ts.work_type)}
                                      </Badge>
                                      {getStatusBadge(ts.status)}
                                    </div>
                                    {ts.description && <div className="text-xs text-gray-600 mt-1">{ts.description}</div>}
                                  </div>
                                  <div className="flex items-center gap-2 ml-4">
                                    <span className="font-semibold text-lg">{ts.hours}h</span>
                                    <Button size="sm" variant="ghost" onClick={() => handleEdit(ts)}>
                                      <Edit2 size={14} />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => handleDelete(ts.id)} className="text-red-600">
                                      <Trash2 size={14} />
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Timesheets;
